import DOMPurify from 'dompurify';

const allowedEmailTags = [
  'p',
  'br',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'a',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'img',
  'table',
  'tbody',
  'tr',
  'td',
  'th',
  'style',
];

const allowedEmailAttributes = [
  'href',
  'src',
  'alt',
  'title',
  'style',
  'class',
  'target',
  'width',
  'height',
  // Table layout/presentational attributes used by HTML emails. Without these,
  // DOMPurify strips them and the preview no longer matches the sent email — e.g.
  // a stripped `colspan` collapses the line-item total row so the grand total is
  // no longer aligned under the price column.
  'colspan',
  'rowspan',
  'align',
  'valign',
  'bgcolor',
  'cellpadding',
  'cellspacing',
  'border',
];

const hasTextContent = value => typeof value === 'string' && value.trim().length > 0;

const escapePlainText = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\r\n|\r|\n/g, '<br>');

export const sanitizeEmailHtml = htmlContent => {
  if (!hasTextContent(htmlContent)) return '';

  return DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: allowedEmailTags,
    ALLOWED_ATTR: allowedEmailAttributes,
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange'],
  });
};

export const getRenderableEmailHtml = mail => {
  const htmlContent = mail?.htmlContent || mail?.htmlBody;
  if (hasTextContent(htmlContent)) {
    return sanitizeEmailHtml(htmlContent);
  }

  const textContent = mail?.plainContent || mail?.textBody;
  if (hasTextContent(textContent)) {
    return escapePlainText(textContent);
  }

  return '';
};

export const hasRenderableEmailContent = mail =>
  hasTextContent(mail?.htmlContent) ||
  hasTextContent(mail?.htmlBody) ||
  hasTextContent(mail?.plainContent) ||
  hasTextContent(mail?.textBody);
